import {useCallback} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useReservation} from 'context/reservation';
import {QUERY_CACHE_KEYS,GuestGroup} from '@chekin/core';
import {API, GuestGroupPayload} from '@chekin/api';
import {toastResponseError} from 'utils/common';

export type ErrorRequest = {
  code: string;
  errors: {
    message: string;
  }[];
  message: string;
  status: number;
  status_code: number;
};

function useGuestGroupMutation() {
  const {data: reservation} = useReservation();
  const guestGroupId = reservation?.guest_group_id;
  const queryClient = useQueryClient();

  const {mutate, isPending, error} = useMutation<
    GuestGroup,
    ErrorRequest,
    {guestGroupId: string; payload: GuestGroupPayload}
  >({
    mutationFn: ({payload, guestGroupId}) =>
      API.guestGroup.patchGuestGroup(guestGroupId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_CACHE_KEYS.seasonGuests],
      });
      void queryClient.refetchQueries({
        queryKey: [QUERY_CACHE_KEYS.summary],
      });
      void queryClient.refetchQueries({
        queryKey: [QUERY_CACHE_KEYS.guestGroup],
      });
    },
    onError: error => {
      if (error.status_code === 400) {
        toastResponseError(error);
        console.error(error);
        return;
      }
    },
  });

  const updateGuestGroup = useCallback(
    (payload: GuestGroupPayload, options: Parameters<typeof mutate>[1]) => {
      if (!guestGroupId) return;
      mutate({guestGroupId, payload}, options);
    },
    [mutate, guestGroupId],
  );

  return {
    error,
    isLoading: isPending,
    updateGuestGroup,
  };
}

export {useGuestGroupMutation};
